import React, { useEffect, useState } from "react";
import { Upload, Send, Users, MessageSquare, CheckCircle2, XCircle, AlertCircle, Image as ImageIcon, Rocket, KeyRound, Copy, ExternalLink, BarChart3, RefreshCw, BookOpen, Smartphone, ShieldCheck } from "lucide-react";
import { dataApi } from "../api/client";

/**
 * AdminBulkView — Panel de operaciones masivas.
 * Sección 1: Importar conductores desde CSV (mapeo interactivo + preview + import).
 * Sección 2: WhatsApp masivo (multi-select drivers + template + adjuntar imagen).
 * Sección 3: Lanzamiento — bienvenida masiva a drivers existentes con APK + manual + contraseña temporal.
 */
export default function AdminBulkView() {
  const [tab, setTab] = useState("launch"); // launch | import | broadcast

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl">Operaciones masivas</h1>
          <p className="text-sm text-neutral-500 mt-1">Lanza tu flota, importa conductores o envía comunicados por WhatsApp</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-neutral-200">
        <TabBtn active={tab === "launch"} onClick={() => setTab("launch")} icon={Rocket} label="🚀 Lanzamiento" />
        <TabBtn active={tab === "engagement"} onClick={() => setTab("engagement")} icon={BarChart3} label="📊 Engagement" />
        <TabBtn active={tab === "import"} onClick={() => setTab("import")} icon={Upload} label="Importar conductores" />
        <TabBtn active={tab === "broadcast"} onClick={() => setTab("broadcast")} icon={MessageSquare} label="WhatsApp masivo" />
      </div>

      {tab === "launch" ? <LaunchSection /> :
       tab === "engagement" ? <EngagementSection /> :
       tab === "import" ? <BulkImportSection /> : <BroadcastSection />}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
      active ? "border-[#5C6BC0] text-[#5C6BC0]" : "border-transparent text-neutral-500 hover:text-neutral-800"
    }`}
    data-testid={`tab-${label}`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

// ═════════════════════════════════════════════════════════════════════════
// BULK IMPORT
// ═════════════════════════════════════════════════════════════════════════
function BulkImportSection() {
  const [rawText, setRawText] = useState("");
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sendCredentials, setSendCredentials] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState("https://turismo.mdd.analytica-ec.com/download");
  const [messageTemplate, setMessageTemplate] = useState(
    "🚕 *Bienvenido a Turismo MDD*\n\nTu cuenta está lista:\n\n👤 *Usuario:* {user}\n🔐 *Contraseña:* {password}\n\n📱 *Descarga la app:*\n{link}\n\n¡Bienvenido a bordo! 🎉"
  );

  // Parsea CSV / TSV / múltiples separadores
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    // Detectar separador (tab, coma o punto y coma)
    const sep = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
    const headers = lines[0].split(sep).map((h) => h.trim().toLowerCase());
    // Mapa flexible de encabezados
    const H = {
      name: ["nombre", "nombre completo", "name"],
      phone: ["celular", "phone", "movil", "nro movil", "numero", "telefono"],
      dni: ["dni", "documento"],
      plate: ["placa", "plate"],
      brand: ["marca", "brand"],
      model: ["modelo", "model"],
      color: ["color"],
      passenger_capacity: ["pasajeros", "pasajeros_capacity", "capacidad"],
    };
    const idx = {};
    for (const [k, aliases] of Object.entries(H)) {
      idx[k] = headers.findIndex((h) => aliases.some((a) => h.includes(a)));
    }
    const parsed = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(sep).map((c) => c.trim());
      if (!cols[idx.name]) continue;
      parsed.push({
        name: cols[idx.name] || "",
        phone: cols[idx.phone] || "",
        dni: cols[idx.dni] || "",
        plate: cols[idx.plate] || "",
        brand: cols[idx.brand] || "",
        model: cols[idx.model] || "",
        color: cols[idx.color] || "",
        passenger_capacity: parseInt(cols[idx.passenger_capacity] || "4", 10) || 4,
        vehicle_type: "sedan",
      });
    }
    return parsed;
  };

  const preview = () => {
    setResult(null);
    setRows(parseCSV(rawText));
  };

  const submit = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    try {
      const r = await dataApi.bulkImportDrivers({
        rows: rows.filter((r) => r.phone),  // sólo con teléfono
        default_country_prefix: "+51",
        send_credentials_whatsapp: sendCredentials,
        app_download_url: downloadUrl,
        message_template: messageTemplate,
      });
      setResult(r);
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
        <div className="font-semibold text-blue-900 mb-1">📄 Formato esperado (CSV/Excel)</div>
        <div className="text-blue-800">
          Copia y pega desde Excel (Ctrl+C). Columnas reconocidas: <code>Nombre / Celular / DNI / Placa / Marca / Modelo / Color / Pasajeros</code>.
          Los celulares peruanos se normalizan a <code>+51 XXX XXX XXX</code>. Se genera contraseña de 6 dígitos por cada conductor.
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
          Pega aquí (con encabezados)
        </label>
        <textarea
          value={rawText} onChange={(e) => setRawText(e.target.value)}
          rows={8} placeholder={"Nombre\tCelular\tDNI\tPlaca\tMarca\tModelo\tColor\tPasajeros\nJuan Perez\t987654321\t11111111\tX3U-473\tToyota\tYaris\tRojo\t4"}
          className="w-full border border-neutral-300 rounded-lg p-3 font-mono text-xs"
          data-testid="bulk-csv-input"
        />
        <button onClick={preview} className="mt-2 btn-primary">Previsualizar</button>
      </div>

      {rows.length > 0 && (
        <>
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-neutral-200 bg-neutral-50 font-semibold text-sm">
              Vista previa: {rows.length} filas
            </div>
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 sticky top-0">
                  <tr>
                    {["Nombre", "Celular", "DNI", "Placa", "Marca", "Modelo", "Color", "Pasaj."].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-semibold text-neutral-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-neutral-100">
                      <td className="px-3 py-1.5">{r.name}</td>
                      <td className="px-3 py-1.5 font-mono">{r.phone}</td>
                      <td className="px-3 py-1.5 font-mono">{r.dni}</td>
                      <td className="px-3 py-1.5 font-mono">{r.plate}</td>
                      <td className="px-3 py-1.5">{r.brand}</td>
                      <td className="px-3 py-1.5">{r.model}</td>
                      <td className="px-3 py-1.5">{r.color}</td>
                      <td className="px-3 py-1.5 text-center">{r.passenger_capacity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={sendCredentials} onChange={(e) => setSendCredentials(e.target.checked)} data-testid="send-credentials-toggle" />
                Enviar credenciales por WhatsApp automáticamente
              </label>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">Link de descarga app</label>
              <input value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)} className="office-input" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
              Plantilla del mensaje (placeholders: {"{name}"} {"{user}"} {"{password}"} {"{link}"})
            </label>
            <textarea value={messageTemplate} onChange={(e) => setMessageTemplate(e.target.value)} rows={6} className="w-full border border-neutral-300 rounded-lg p-3 text-sm font-mono" />
          </div>

          <button onClick={submit} disabled={importing}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3"
            data-testid="submit-bulk-import">
            <Upload className="w-4 h-4" />
            {importing ? "Importando..." : `Importar ${rows.length} conductores`}
          </button>
        </>
      )}

      {result && (
        <div className="bg-white border border-neutral-200 rounded-lg p-4 space-y-2">
          <div className="font-semibold">Resultado del import</div>
          <div className="grid grid-cols-3 gap-2">
            <StatBox label="Creados" value={result.summary?.created || 0} color="green" icon={CheckCircle2} />
            <StatBox label="Duplicados" value={result.summary?.skipped || 0} color="yellow" icon={AlertCircle} />
            <StatBox label="Errores" value={result.summary?.errors || 0} color="red" icon={XCircle} />
          </div>
          {result.created?.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm font-semibold cursor-pointer">Ver contraseñas generadas ({result.created.length})</summary>
              <table className="w-full text-xs mt-2">
                <thead className="bg-neutral-50"><tr><th className="p-2 text-left">Nombre</th><th className="p-2 text-left">Usuario</th><th className="p-2 text-left">Password</th></tr></thead>
                <tbody>
                  {result.created.map((c) => (
                    <tr key={c.user_id} className="border-t"><td className="p-2">{c.name}</td><td className="p-2 font-mono">{c.phone}</td><td className="p-2 font-mono font-bold">{c.generated_password}</td></tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

const StatBox = ({ label, value, color, icon: Icon }) => (
  <div className={`p-3 rounded-lg border bg-${color}-50 border-${color}-200`}>
    <div className="flex items-center gap-2 text-xs font-semibold text-neutral-600 uppercase">
      <Icon className={`w-4 h-4 text-${color}-600`} />{label}
    </div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════
// BROADCAST
// ═════════════════════════════════════════════════════════════════════════
function BroadcastSection() {
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    dataApi.users().then((all) => setDrivers(all.filter((u) => ["driver", "socio"].includes(u.role) && u.is_active)));
  }, []);

  const filtered = drivers.filter((d) =>
    (d.name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (d.phone || "").includes(filter)
  );
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.id)));
  };
  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const submit = async () => {
    if (!message.trim()) return alert("Escribe un mensaje");
    if (selected.size === 0 && !confirm("¿Enviar a TODOS los conductores activos?")) return;
    setSending(true);
    try {
      const r = await dataApi.broadcastWhatsApp({
        driver_ids: selected.size === 0 ? null : [...selected],
        message,
        image_url: imageUrl || null,
      });
      setResult(r);
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Lista drivers */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between bg-neutral-50">
          <div>
            <div className="font-semibold text-sm">Destinatarios · {selected.size}/{filtered.length} seleccionados</div>
            <div className="text-xs text-neutral-500">Selección vacía = enviar a TODOS</div>
          </div>
          <button onClick={toggleAll} className="text-xs font-semibold text-[#5C6BC0]">
            {selected.size === filtered.length && filtered.length > 0 ? "Deseleccionar todo" : "Seleccionar todo"}
          </button>
        </div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar conductor..." className="office-input rounded-none border-x-0 border-t-0" />
        <div className="max-h-96 overflow-auto">
          {filtered.map((d) => (
            <label key={d.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" data-testid={`driver-row-${d.id}`}>
              <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{d.name}</div>
                <div className="text-xs text-neutral-500 font-mono">{d.phone}</div>
              </div>
              <div className={`text-[10px] font-semibold px-2 py-0.5 rounded ${d.role === "socio" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{d.role}</div>
            </label>
          ))}
        </div>
      </div>

      {/* Composer */}
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Mensaje</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} placeholder="Escribe tu mensaje... Puedes usar {name} para personalizar."
            className="w-full border border-neutral-300 rounded-lg p-3 text-sm" data-testid="broadcast-message" />
          <div className="text-xs text-neutral-500 mt-1">Placeholders: {"{name}"} {"{user}"} {"{phone}"}</div>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
            <ImageIcon className="w-4 h-4 inline mr-1" /> URL de imagen (opcional)
          </label>
          <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." className="office-input" data-testid="broadcast-image-url" />
          {imageUrl && <img src={imageUrl} alt="preview" className="mt-2 max-h-32 rounded border" />}
        </div>
        <button onClick={submit} disabled={sending || !message.trim()}
          className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="submit-broadcast">
          <Send className="w-4 h-4" />
          {sending ? "Enviando..." : `Enviar a ${selected.size === 0 ? "TODOS" : selected.size + " conductores"}`}
        </button>

        {result && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="font-semibold mb-2">Resultado</div>
            <div className="grid grid-cols-3 gap-2">
              <StatBox label="Total" value={result.summary?.total || 0} color="blue" icon={Users} />
              <StatBox label="Enviados" value={result.summary?.sent || 0} color="green" icon={CheckCircle2} />
              <StatBox label="Fallidos" value={result.summary?.failed || 0} color="red" icon={XCircle} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// 🚀 LAUNCH — Bienvenida masiva a conductores existentes
// ═════════════════════════════════════════════════════════════════════════
function LaunchSection() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  // URLs con tracking — el endpoint APK redirige a expo.dev registrando la descarga.
  const DEFAULT_APK = `${backendUrl}/api/public/download/driver-apk`;
  const defaultManual = `${backendUrl}/api/public/manual-conductor?apk=${encodeURIComponent(DEFAULT_APK)}&support=51987654321`;

  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [apkUrl, setApkUrl] = useState(DEFAULT_APK);
  const [manualUrl, setManualUrl] = useState(defaultManual);
  const [supportPhone, setSupportPhone] = useState("+51 987 654 321");
  const [resetPasswords, setResetPasswords] = useState(true);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    dataApi.users().then((all) =>
      setDrivers(all.filter((u) => ["driver", "socio"].includes(u.role) && u.is_active && u.phone))
    );
  }, []);

  const filtered = drivers.filter((d) =>
    (d.name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (d.phone || "").includes(filter)
  );
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((d) => d.id)));
  };
  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const buildPayload = () => ({
    driver_ids: selected.size === 0 ? null : [...selected],
    apk_url_driver: apkUrl.trim(),
    manual_url: manualUrl.trim(),
    support_phone: supportPhone.trim(),
    reset_passwords: resetPasswords,
  });

  const doPreview = async () => {
    if (!apkUrl.trim() || !manualUrl.trim()) return alert("APK URL y manual URL son obligatorios");
    setBusy(true); setPreview(null); setResult(null);
    try {
      const r = await dataApi.onboardingWelcome({ ...buildPayload(), dry_run: true });
      setPreview(r);
    } catch (e) { alert("Error preview: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!preview) return alert("Primero genera un preview con 'Vista previa'");
    const total = preview.summary.total;
    const msg = resetPasswords
      ? `⚠️ Vas a ENVIAR bienvenida a ${total} conductores Y RESETEAR sus contraseñas. Esta acción es irreversible. ¿Continuar?`
      : `Vas a ENVIAR bienvenida a ${total} conductores (sin resetear contraseñas). ¿Continuar?`;
    if (!confirm(msg)) return;
    setBusy(true); setResult(null);
    try {
      const r = await dataApi.onboardingWelcome({ ...buildPayload(), dry_run: false });
      setResult(r);
      setPreview(null);
    } catch (e) { alert("Error envío: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const copy = async (txt) => { try { await navigator.clipboard.writeText(txt); } catch { /* ignore */ } };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Config */}
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Rocket className="w-5 h-5 text-orange-600" />
            <div className="font-bold text-orange-900">Modo lanzamiento</div>
          </div>
          <p className="text-xs text-orange-800 leading-relaxed">
            Envía a cada conductor su usuario, contraseña temporal, link de la APK y del manual paso a paso.
            <strong> Genera contraseñas nuevas y las guarda hasheadas.</strong>
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
            <ExternalLink className="w-3.5 h-3.5 inline mr-1" /> URL de descarga APK Driver
          </label>
          <div className="flex gap-2">
            <input value={apkUrl} onChange={(e) => setApkUrl(e.target.value)}
                   className="office-input flex-1 font-mono text-xs" data-testid="launch-apk-url" />
            <button onClick={() => copy(apkUrl)} className="px-3 border rounded hover:bg-neutral-50" title="Copiar"><Copy className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
            📖 URL del manual (público)
          </label>
          <div className="flex gap-2">
            <input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)}
                   className="office-input flex-1 font-mono text-xs" data-testid="launch-manual-url" />
            <a href={manualUrl} target="_blank" rel="noreferrer" className="px-3 border rounded hover:bg-neutral-50 inline-flex items-center" title="Abrir en pestaña">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
            📞 Teléfono de soporte
          </label>
          <input value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)}
                 className="office-input" placeholder="+51 987 654 321" data-testid="launch-support-phone" />
        </div>

        <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
          <input type="checkbox" checked={resetPasswords} onChange={(e) => setResetPasswords(e.target.checked)} data-testid="launch-reset-passwords" />
          <div>
            <div className="text-sm font-semibold flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Resetear contraseñas</div>
            <div className="text-xs text-neutral-500">Genera contraseña temporal nueva por cada driver. Se envía por WhatsApp.</div>
          </div>
        </label>

        <div className="flex gap-2 pt-2">
          <button onClick={doPreview} disabled={busy}
                  className="flex-1 py-3 border-2 border-[#5C6BC0] text-[#5C6BC0] rounded-lg font-bold hover:bg-[#5C6BC0]/5" data-testid="launch-preview-btn">
            {busy ? "..." : "🔍 Vista previa"}
          </button>
          <button onClick={doSend} disabled={busy || !preview}
                  className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-orange-700" data-testid="launch-send-btn">
            {busy ? "..." : `🚀 Enviar${preview ? ` a ${preview.summary.total}` : ""}`}
          </button>
        </div>
      </div>

      {/* Lista drivers + preview */}
      <div className="space-y-3">
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between bg-neutral-50">
            <div>
              <div className="font-semibold text-sm">Destinatarios · {selected.size}/{filtered.length}</div>
              <div className="text-xs text-neutral-500">Vacío = TODOS los conductores activos con teléfono</div>
            </div>
            <button onClick={toggleAll} className="text-xs font-semibold text-[#5C6BC0]" data-testid="launch-toggle-all">
              {selected.size === filtered.length && filtered.length > 0 ? "Deseleccionar" : "Seleccionar todo"}
            </button>
          </div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar..." className="office-input rounded-none border-x-0 border-t-0" />
          <div className="max-h-72 overflow-auto">
            {filtered.map((d) => (
              <label key={d.id} className="flex items-center gap-3 px-4 py-2 border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" data-testid={`launch-driver-${d.id}`}>
                <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{d.name}</div>
                  <div className="text-xs text-neutral-500 font-mono">{d.phone}</div>
                </div>
                <div className={`text-[10px] font-semibold px-2 py-0.5 rounded ${d.role === "socio" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{d.role}</div>
              </label>
            ))}
            {filtered.length === 0 && <div className="p-6 text-center text-sm text-neutral-500">Sin conductores activos con teléfono</div>}
          </div>
        </div>

        {preview && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="launch-preview-panel">
            <div className="font-bold text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Vista previa (nada se ha enviado)
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <StatBox label="Destinatarios" value={preview.summary.total} color="blue" icon={Users} />
              <StatBox label="Con teléfono" value={preview.summary.total - preview.summary.failed} color="green" icon={CheckCircle2} />
              <StatBox label="Sin teléfono" value={preview.summary.failed} color="red" icon={XCircle} />
            </div>
            {preview.previews?.[0] && (
              <details className="bg-white rounded p-3 border">
                <summary className="cursor-pointer font-semibold text-xs">📱 Ver mensaje de ejemplo ({preview.previews[0].name})</summary>
                <pre className="text-[11px] whitespace-pre-wrap mt-2 font-mono text-neutral-700 leading-relaxed">{preview.previews[0].message}</pre>
              </details>
            )}
          </div>
        )}

        {result && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="launch-result-panel">
            <div className="font-bold mb-2">Resultado envío</div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <StatBox label="Total" value={result.summary.total} color="blue" icon={Users} />
              <StatBox label="Enviados" value={result.summary.sent} color="green" icon={CheckCircle2} />
              <StatBox label="Fallidos" value={result.summary.failed} color="red" icon={XCircle} />
            </div>
            {result.failed?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-semibold text-red-700">Ver fallidos ({result.failed.length})</summary>
                <ul className="text-xs mt-1 space-y-1">
                  {result.failed.map((f, i) => (
                    <li key={i} className="text-neutral-600"><span className="font-mono">{f.phone}</span> — {f.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// 📊 ENGAGEMENT — Estado de instalación / login por conductor
// ═════════════════════════════════════════════════════════════════════════
function EngagementSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await dataApi.driversEngagement();
      setData(r);
      setLastRefresh(new Date());
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    // Auto-refresh cada 15s durante el lanzamiento
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "hace segundos";
    if (diffMin < 60) return `hace ${diffMin} min`;
    if (diffMin < 60 * 24) return `hace ${Math.floor(diffMin / 60)} h`;
    return d.toLocaleDateString("es-PE", { day: "numeric", month: "short" });
  };

  if (busy && !data) return <div className="p-6 text-center text-neutral-500">Cargando...</div>;
  if (!data) return null;

  const s = data.summary;
  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard label="Bienvenida enviada" value={s.welcome_sent} total={s.total} color="blue" icon={Send} />
        <KPICard label="Abrieron manual" value={s.manual_opened} total={s.total} color="purple" icon={BookOpen} />
        <KPICard label="Descargaron APK" value={s.apk_downloaded} total={s.total} color="orange" icon={Smartphone} />
        <KPICard label="Iniciaron sesión" value={s.logged_in} total={s.total} color="green" icon={CheckCircle2} />
        <KPICard label="Cambiaron password" value={s.password_changed} total={s.total} color="teal" icon={ShieldCheck} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-500">
          {lastRefresh ? `Última actualización: ${lastRefresh.toLocaleTimeString("es-PE")} · auto cada 15s` : ""}
        </div>
        <button onClick={load} className="px-3 py-1.5 border rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-neutral-50" data-testid="engagement-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {/* Driver table */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs font-semibold text-neutral-600 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Conductor</th>
              <th className="px-3 py-2 text-center">📨 WA</th>
              <th className="px-3 py-2 text-center">📖 Manual</th>
              <th className="px-3 py-2 text-center">📥 APK</th>
              <th className="px-3 py-2 text-center">🔑 Login</th>
              <th className="px-3 py-2 text-center">🛡️ Pwd</th>
              <th className="px-3 py-2 text-left">Última actividad</th>
            </tr>
          </thead>
          <tbody>
            {data.drivers.map((d) => {
              const passwordChanged = d.welcome_sent_at && !d.must_change_password;
              const lastActivity = d.last_login || d.apk_last_downloaded_at || d.manual_last_opened_at;
              return (
                <tr key={d.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`engagement-row-${d.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold">{d.name}</div>
                    <div className="text-xs text-neutral-500 font-mono">{d.phone || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {d.welcome_sent_at ? <span title={fmtTime(d.welcome_sent_at)}>✅</span> : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {d.manual_opens_count > 0 ? (
                      <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-bold" title={fmtTime(d.manual_last_opened_at)}>
                        {d.manual_opens_count}×
                      </span>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {d.apk_downloads_count > 0 ? (
                      <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold" title={fmtTime(d.apk_last_downloaded_at)}>
                        {d.apk_downloads_count}×
                      </span>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {d.last_login ? <span className="text-green-600 font-bold" title={fmtTime(d.last_login)}>✅</span> : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {passwordChanged ? <span className="text-teal-600 font-bold">✅</span> :
                     d.welcome_sent_at ? <span className="text-amber-500" title="Aún tiene la contraseña temporal">⏳</span> :
                     <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-neutral-600">
                    {fmtTime(lastActivity)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.drivers.length === 0 && (
          <div className="p-8 text-center text-sm text-neutral-500">Sin conductores registrados</div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <div className="font-bold mb-1 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Cómo funciona el tracking</div>
        <ul className="text-xs list-disc list-inside space-y-1 opacity-90">
          <li>Cuando envías la bienvenida, cada URL lleva un <code>?d=&lt;driver_id&gt;</code> único.</li>
          <li>Al tocar el link del manual, se cuenta la apertura en <code>driver_engagement</code>.</li>
          <li>Al tocar el link de la APK, se registra la descarga y se redirige a Expo.</li>
          <li>El login aparece cuando el conductor entra por primera vez; el password cambia cuando completa el cambio forzado.</li>
        </ul>
      </div>
    </div>
  );
}

function KPICard({ label, value, total, color, icon: Icon }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const colors = {
    blue: "from-blue-50 to-blue-100 border-blue-300 text-blue-700",
    purple: "from-purple-50 to-purple-100 border-purple-300 text-purple-700",
    orange: "from-orange-50 to-orange-100 border-orange-300 text-orange-700",
    green: "from-green-50 to-green-100 border-green-300 text-green-700",
    teal: "from-teal-50 to-teal-100 border-teal-300 text-teal-700",
  }[color] || "from-neutral-50 to-neutral-100 border-neutral-300 text-neutral-700";
  return (
    <div className={`bg-gradient-to-br ${colors} border rounded-xl p-3`} data-testid={`engagement-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5" /><span className="text-[10px] font-bold uppercase tracking-wider">{label}</span></div>
      <div className="text-2xl font-black">{value}<span className="text-sm font-semibold opacity-60">/{total}</span></div>
      <div className="text-xs font-semibold opacity-70 mt-0.5">{pct}%</div>
    </div>
  );
}

